/**
 * Intercom Messenger integration.
 *
 * Drops the Intercom widget into the page and identifies the logged-in user
 * so the web channel can receive banners and in-app messages from campaigns
 * configured in the Intercom dashboard.
 *
 * Mirrors the structure of utils/analytics/analytics.ts — module-level state,
 * an init function called from main.ts, identify/reset hooks called from the
 * Vuex setUser mutation and logout action, and an update hook called from
 * router.afterEach.
 *
 * Out of scope (handled separately):
 *   - Pre-syncing user attributes from Core into Intercom (backend epic)
 *   - Forwarding PostHog events into Intercom (backend epic)
 *   - Native mobile push (mobile app via CR-70)
 */

type IntercomCommand = {
  (...args: unknown[]): void;
  q?: unknown[][];
  c?: (args: unknown[]) => void;
};

declare global {
  // Declaration-merging on the built-in Window interface cannot be done with a
  // `type` alias — interface is the only form TypeScript will merge here.
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    Intercom?: IntercomCommand;
    intercomSettings?: Record<string, unknown>;
    attachEvent?: (event: string, handler: () => void) => void;
  }
}

export type IntercomIdentifyContext = {
  userId: string;
  clientId: string;
  userRole?: string;
  lang?: string;
  skin?: number;
  /**
   * HMAC-SHA256(user_id, workspace_secret) computed server-side. When present,
   * Intercom verifies the identity claim and rejects impersonation attempts.
   * Optional until the backend ships the hash on the login / me response —
   * tracked as the open AC item on this ticket.
   */
  userHash?: string;
};

const appId = process.env.VUE_APP_INTERCOM_APP_ID || "";
const apiPath = process.env.VUE_APP_API_PATH || "unknown";

/**
 * Regional data hosting. An Intercom workspace is region-locked, so the
 * Messenger must talk to the matching region endpoint or it silently fails to
 * load. The mobile app sets this via the native SDK's `intercomRegion` enum;
 * the web Messenger instead takes an `api_base` URL, so we map the shared
 * VUE_APP_INTERCOM_REGION code (eu/au/us) to its endpoint. Unset → Intercom's
 * US default.
 */
const INTERCOM_API_BASE_BY_REGION: Record<string, string> = {
  us: "https://api-iam.intercom.io",
  eu: "https://api-iam.eu.intercom.io",
  au: "https://api-iam.au.intercom.io",
};
const apiBase =
  INTERCOM_API_BASE_BY_REGION[
    (process.env.VUE_APP_INTERCOM_REGION || "").toLowerCase()
  ];

/**
 * Bupa is a white-labelled tenant and is excluded from Intercom messaging.
 * If a Bupa user identifies, we shut Intercom down so the widget never appears.
 */
const BUPA_SKIN = 13;

/**
 * Intercom is disabled in three cases:
 *   1. Local development (yarn serve runs with NODE_ENV=development)
 *   2. Explicit env opt-out (VUE_APP_INTERCOM_ENABLED=false — used for AUS
 *      until Marketing confirms the AUS workspace is ready)
 *   3. No app id configured (defensive fallback so a missing env var
 *      cannot accidentally boot against a stale workspace)
 *
 * Gating on NODE_ENV !== "development" mirrors the PostHog setup — QA and
 * staging envs set NODE_ENV=staging, which still enables Intercom.
 */
const intercomEnabled =
  process.env.NODE_ENV !== "development" &&
  process.env.VUE_APP_INTERCOM_ENABLED !== "false" &&
  Boolean(appId);

let initialised = false;
let bootedUserId: string | null = null;

const isExcludedTenant = (skin?: number): boolean => skin === BUPA_SKIN;

/**
 * Install the Intercom Messenger loader on window. Sets up the call queue
 * (window.Intercom) before the widget script loads, so calls made before the
 * script resolves are replayed once it does. Idempotent.
 *
 * Implementation follows the official Intercom JavaScript snippet — preserved
 * verbatim from the previous App.vue inline boot so behaviour is unchanged.
 */
export const initialiseIntercom = (): void => {
  if (initialised) return;
  if (!intercomEnabled) {
    initialised = true;
    return;
  }

  const existing = window.Intercom;
  if (typeof existing === "function") {
    existing("reattach_activator");
    existing("update", window.intercomSettings);
    initialised = true;
    return;
  }

  const queue: IntercomCommand = ((...args: unknown[]): void => {
    queue.c?.(args);
  }) as IntercomCommand;
  queue.q = [];
  queue.c = (args): void => {
    queue.q?.push(args);
  };
  window.Intercom = queue;

  const loadWidget = (): void => {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${appId}`;
    const firstScript = document.getElementsByTagName("script")[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);
  };

  if (document.readyState === "complete") {
    loadWidget();
  } else {
    window.addEventListener("load", loadWidget, false);
  }

  initialised = true;
};

/**
 * Boot Intercom with the logged-in user's identity. Called from the Vuex
 * setUser mutation, so it fires for every authentication entry point —
 * login, register, OAuth referral, and rehydration on fresh page load.
 *
 * Marketing pre-syncs core attributes (plan, tenant, region) from Core into
 * Intercom out-of-band — we only send identity here, not the full profile.
 *
 * Bupa members (skin 13) are excluded — if a Bupa user identifies we shut
 * Intercom down so the widget never appears.
 */
export const identifyIntercomUser = (
  context: IntercomIdentifyContext,
): void => {
  if (!intercomEnabled) return;
  if (!window.Intercom) return;

  if (isExcludedTenant(context.skin)) {
    resetIntercomUser();
    return;
  }

  bootedUserId = context.userId;

  const payload: Record<string, unknown> = {
    app_id: appId,
    user_id: context.userId,
    environment: apiPath,
  };

  // Omitted when unset so Intercom falls back to its US default endpoint.
  if (apiBase) {
    payload.api_base = apiBase;
  }

  if (context.clientId) {
    payload.company = { company_id: context.clientId };
  }

  // Identity verification — sent when the backend provides the HMAC hash on
  // login / me. Until then Intercom runs in "unverified" mode (AC open).
  if (context.userHash) {
    payload.user_hash = context.userHash;
  }

  window.Intercom("boot", payload);
};

/**
 * Re-evaluate which Intercom messages should display for the current user.
 * Called from router.afterEach on SPA navigation. Safe to fire on every
 * route change — Intercom rate-limits server-side.
 */
export const updateIntercom = (): void => {
  if (!intercomEnabled) return;
  if (!window.Intercom) return;
  if (!bootedUserId) return;
  window.Intercom("update", {
    last_request_at: Math.floor(Date.now() / 1000),
  });
};

/**
 * Clear the Intercom session and remove the widget. Called from the Vuex
 * logout action for every logout — not just Bupa, as it was previously.
 */
export const resetIntercomUser = (): void => {
  bootedUserId = null;
  if (!intercomEnabled) return;
  if (!window.Intercom) return;
  window.Intercom("shutdown");
};
